/**
 * Новая тема corvette. Сложнее и имеет больше параметров чем golf. Перед использованием
 * должна пройти нормализацию для удобного использования
 */
import map from '@tinkoff/utils/object/map'
import { AppTheme, BreakpointAlias, Breakpoints, getCurrentBreakpointAlias } from 'application/theme/index'
import { getValueForBreakpoint } from 'lib/get-value-for-breakpoint'

import { CorvetteBreakpoints } from '../appTheme'
import { createMediaQueries, extractPureTextStyles } from '../golf/golfToAppTheme'
import { multiplyRems } from '../utils'
import { CorvetteTheme, GridBreakpoint, NormalizedCorvetteGridBreakpoints, TextStyle } from './types'

/**
 * Значения в дизайн-системе указаны из расчёта того, что 1rem равен 16px
 * Однако, в настоящий момент (01.09.2021) на боевых сайтах используется значение в 10px
 * Чтобы визуальный размер элементов оказался равен тому, что нарисован в макетах исходя из rem = 16px, нам нужно
 * Умножить все значения из дизайн-системы на 1.6
 */
const DESIGN_SYSTEM_IN_OLD_ENVINROMENT_REM_RATIO = 1.6

/**
 * В теме corvette брейкпоинты имеет такой вид {@link CorvetteBreakpoints}
 * Т.к. использовать их очень неудобно, они нормализуются, после чего
 * к брейкпоинту можно обратиться по его алиасу, а не по минимальной ширине экрана
 * @param breakpoints
 * @returns
 */
export const normalizeBreakpoints = (
  breakpoints: CorvetteTheme['grid']['breakpoints']
): NormalizedCorvetteGridBreakpoints => {
  return Object.fromEntries(
    Object.entries(breakpoints).map(([property, value]) => [
      value.alias[1],
      { minWidth: property === '_' ? '0px' : property, ...value }
    ])
  ) as NormalizedCorvetteGridBreakpoints
}

/**
 * У брейкпоинтов в TextStyles такой же принцип, как и у grid
 * (название брейкпоинта - его минимальная ширина). Из-за этого
 * при нормализации textStyles для начала надо привести брейкпоинты
 * в стандартный вид
 * @param textStyle
 * @param breakpoints
 * @returns
 */
export const normalizeTextStyleBreakpointsNames = (
  textStyle: CorvetteBreakpoints<TextStyle>,
  breakpoints: CorvetteBreakpoints<GridBreakpoint>
): Breakpoints<TextStyle> => {
  return Object.fromEntries(
    Object.entries(textStyle).map(([rawBreakpointName, textStyleValue]) => {
      const gridBreakpoint = breakpoints[rawBreakpointName]
      if (!gridBreakpoint) {
        throw new Error('Text style has invalid breakpoint')
      } else {
        return [gridBreakpoint.alias[1], textStyleValue]
      }
    })
  ) as Breakpoints<TextStyle>
}

/**
 * Функция меняет каждый брейкпоинт так, что мы можем получить все свойства прямо из него,
 * и нам не нужно смотреть на уже примененные брейкпоинты, их свойства уже есть в текущем
 * @param textStyle
 * @returns
 */
export const makeTextStylesBreakpointsIndependent = (textStyle: Breakpoints<TextStyle>): Breakpoints<TextStyle> => {
  let accumulatedStyles = {}
  const textStyleWithIndependentBreakpoints = map((previousBreakpointStyle) => {
    accumulatedStyles = { ...accumulatedStyles, ...previousBreakpointStyle }
    // Нельзя использовать accumulatedStyles, т.к. их тип остается всегда {}
    return { ...accumulatedStyles, ...previousBreakpointStyle }
  }, textStyle)

  return textStyleWithIndependentBreakpoints
}

export const normalizeTextStyleTypography = (textStyle: TextStyle, typography: CorvetteTheme['typography']) => {
  return {
    fontWeight: typography.fontWeights[textStyle.fontWeight],
    lineHeight: multiplyRems(typography.lineHeights[textStyle.lineHeight], DESIGN_SYSTEM_IN_OLD_ENVINROMENT_REM_RATIO),
    fontSize: multiplyRems(typography.fontSizes[textStyle.fontSize], DESIGN_SYSTEM_IN_OLD_ENVINROMENT_REM_RATIO),
    fontFamily:
      typography.fontFamily[textStyle.fontFamily] +
      ',-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto" ,"Helvetica Neue", Arial, sans-serif,  "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
    marginTop: textStyle.marginTop * DESIGN_SYSTEM_IN_OLD_ENVINROMENT_REM_RATIO,
    marginBottom: textStyle.marginBottom * DESIGN_SYSTEM_IN_OLD_ENVINROMENT_REM_RATIO
  }
}

const getFontByFamily = (fontFamily: string, fontFace: CorvetteTheme['font-face']) => {
  const font = fontFace.fonts.find((font) => font.family === fontFamily)

  if (!font) throw Error(`Theming error: font with font-family ${fontFamily} was requested but not declared in theme`)

  return {
    ...font,
    family:
      font.family +
      ',-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto" ,"Helvetica Neue", Arial, sans-serif,  "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"'
  }
}

export const normalizeGrid = (grid: CorvetteTheme['grid'], currentBreakpointName: BreakpointAlias) => {
  const normalizedBreakpoints = normalizeBreakpoints(grid.breakpoints)
  const currentBreakpointSettings = getValueForBreakpoint(normalizedBreakpoints, currentBreakpointName)

  return {
    columns: grid.columns,
    gutters: multiplyRems(currentBreakpointSettings.gutters, DESIGN_SYSTEM_IN_OLD_ENVINROMENT_REM_RATIO),
    containerGutters: multiplyRems(
      currentBreakpointSettings.containerGutters,
      DESIGN_SYSTEM_IN_OLD_ENVINROMENT_REM_RATIO
    ),
    containerWidth: grid.container
  }
}

/**
 * Создаёт объект темы для конкретного брейкпоинта, взяв за основу объект темы проекта
 * Автоматически извлекает только те значения, которые применимы на текущей ширине экрана
 * @param theme
 * @param screenWidth
 * @returns
 */
export const corvetteToAppTheme = (theme: CorvetteTheme, screenWidth: number): AppTheme => {
  const currentBreakpointName = getCurrentBreakpointAlias(normalizeBreakpoints(theme.grid.breakpoints), screenWidth)

  const { h1, h2, h3, h4, h5, h6, p1, p2, p3, p4, btn } = theme['text-styles']

  const normalizedStyles = map(
    (style) => {
      const styleWithNormalizedBreakpointsNames = normalizeTextStyleBreakpointsNames(style, theme.grid.breakpoints)
      const styleWithIndependentBreakpoints = makeTextStylesBreakpointsIndependent(styleWithNormalizedBreakpointsNames)
      const styleWithOnlyCurrentBreakpoint = getValueForBreakpoint(
        styleWithIndependentBreakpoints,
        currentBreakpointName
      )
      return normalizeTextStyleTypography(styleWithOnlyCurrentBreakpoint, theme.typography)
    },
    { h1, h2, h3, h4, h5, h6, p1, p2, p3, p4, btn }
  )

  return {
    mediaQueries: createMediaQueries(normalizeBreakpoints(theme.grid.breakpoints)),
    activeBreakpoint: currentBreakpointName,
    grid: normalizeGrid(theme.grid, currentBreakpointName),
    palette: theme.colors,
    fonts: {
      heading: getFontByFamily(theme.typography.fontFamily.heading, theme['font-face']),
      body: getFontByFamily(theme.typography.fontFamily.body, theme['font-face']),
      accent: getFontByFamily(theme.typography.fontFamily.accent, theme['font-face'])
    },
    textStyles: {
      h1: extractPureTextStyles(normalizedStyles.h1),
      h2: extractPureTextStyles(normalizedStyles.h2),
      h3: extractPureTextStyles(normalizedStyles.h3),
      h4: extractPureTextStyles(normalizedStyles.h4),
      h5: extractPureTextStyles(normalizedStyles.h5),
      h6: extractPureTextStyles(normalizedStyles.h6),
      p1: extractPureTextStyles(normalizedStyles.p1),
      p2: extractPureTextStyles(normalizedStyles.p2),
      p3: extractPureTextStyles(normalizedStyles.p3),
      p4: extractPureTextStyles(normalizedStyles.p4),
      btn: extractPureTextStyles(normalizedStyles.btn)
    },
    typography: {
      title: {
        h1: normalizedStyles.h1,
        h2: normalizedStyles.h2,
        h3: normalizedStyles.h3,
        h4: normalizedStyles.h4,
        h5: normalizedStyles.h5,
        h6: normalizedStyles.h6
      },
      paragraph: {
        p1: normalizedStyles.p1,
        p2: normalizedStyles.p2,
        p3: normalizedStyles.p3,
        p4: normalizedStyles.p4
      }
    },
    transition: theme.transition,
    shadows: theme.shadows,
    spacing: map((spacing) => {
      return multiplyRems(spacing, DESIGN_SYSTEM_IN_OLD_ENVINROMENT_REM_RATIO)
    }, theme.spacing)
  }
}
